import React, { useEffect, useState } from "react";
import { Card, CardBody, CardTitle, Row, Col } from "reactstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const UserMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const fetchUserMetrics = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("Access token not found. Please log in as admin.");
      }

      const response = await fetch(`${API_BASE_URL}/admin/dashboard/user-metrics`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized: Please log in as admin.");
        } else {
          throw new Error(`Error fetching user metrics: ${response.statusText}`);
        }
      }

      const data = await response.json();
      setMetrics(data);
    } catch (err) {
      console.error("User metrics fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserMetrics();
  }, []);

  if (loading) return <div className="p-4"><p>Loading User Metrics...</p></div>;
  if (error) return <div className="p-4"><p style={{ color: "red" }}>{error}</p></div>;
  if (!metrics) return <div className="p-4"><p style={{ color: "red" }}>No data available.</p></div>;

  return (
    <div className="p-4">
      <h2 className="mb-4">User Metrics</h2>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md="3" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                Total Verified Users
              </CardTitle>
              <h4 className="fw-bold">{metrics.totalVerifiedUsers?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                Pending Verifications
              </CardTitle>
              <h4 className="fw-bold text-warning">{metrics.pendingVerifications?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                Daily Active Users
              </CardTitle>
              <h4 className="fw-bold text-primary">{metrics.activeUsers?.daily?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md="3" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                Weekly Active Users
              </CardTitle>
              <h4 className="fw-bold text-info">{metrics.activeUsers?.weekly?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* New Users Cards */}
      <Row className="mb-4">
        <Col md="4" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                New Users (24 Hours)
              </CardTitle>
              <h4 className="fw-bold">{metrics.newUsers?.oneDay?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md="4" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                New Users (Past Week)
              </CardTitle>
              <h4 className="fw-bold">{metrics.newUsers?.oneWeek?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
        <Col md="4" sm="6" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted">
                New Users (Past Month)
              </CardTitle>
              <h4 className="fw-bold">{metrics.newUsers?.oneMonth?.toLocaleString() || 0}</h4>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Monthly Active Users */}
      <Row className="mb-4">
        <Col md="12" className="mb-3">
          <Card className="shadow-sm border-0">
            <CardBody>
              <CardTitle tag="h6" className="text-muted mb-3">
                Monthly Active Users
              </CardTitle>
              <h3 className="fw-bold">{metrics.activeUsers?.monthly?.toLocaleString() || 0}</h3>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Chart */}
      {metrics.chart?.verifiedUsersOverTime && metrics.chart.verifiedUsersOverTime.length > 0 && (
        <Row className="mt-4">
          <Col md="12" className="mb-4">
            <Card className="shadow-sm border-0">
              <CardBody>
                <CardTitle tag="h5" className="mb-3">
                  Total Verified Users Over Time
                </CardTitle>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={metrics.chart.verifiedUsersOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#007bff" 
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default UserMetrics;
